/**
 * Browser Notification Service
 * Handles browser push notifications for admin alerts
 */

export type BrowserNotificationPermission = "granted" | "denied" | "default";

/**
 * Request permission for browser notifications
 */
export async function requestNotificationPermission(): Promise<BrowserNotificationPermission> {
    if (!("Notification" in window)) {
        console.warn("[BrowserNotification] Browser does not support notifications");
        return "denied";
    }

    if (Notification.permission === "granted") {
        return "granted";
    }

    if (Notification.permission === "denied") {
        return "denied";
    }

    try {
        const permission = await Notification.requestPermission();
        return permission as BrowserNotificationPermission;
    } catch (error) {
        console.error("[BrowserNotification] Error requesting permission:", error);
        return "denied";
    }
}

/**
 * Check current notification permission status
 */
export function getNotificationPermission(): BrowserNotificationPermission {
    if (!("Notification" in window)) {
        return "denied";
    }
    return Notification.permission as BrowserNotificationPermission;
}

/**
 * Show a browser notification
 */
export function showBrowserNotification(
    title: string,
    options?: {
        body?: string;
        icon?: string;
        badge?: string;
        tag?: string;
        data?: unknown;
        requireInteraction?: boolean;
        silent?: boolean;
    }
): Notification | null {
    if (!("Notification" in window)) {
        console.warn("[BrowserNotification] Browser does not support notifications");
        return null;
    }

    if (Notification.permission !== "granted") {
        console.warn("[BrowserNotification] Notification permission not granted. Current state:", Notification.permission);
        return null;
    }

    try {
        console.log("[BrowserNotification] Attempting to show notification:", title, options);
        const notification = new Notification(title, {
            icon: options?.icon || "/favicon.svg",
            badge: options?.badge || "/favicon.svg",
            body: options?.body,
            tag: options?.tag,
            data: options?.data,
            requireInteraction: options?.requireInteraction ?? true, // Force interaction by default for admin alerts
            silent: options?.silent ?? false,
        });

        console.log("[BrowserNotification] Notification created successfully", notification);
        return notification;
    } catch (error) {
        console.error("[BrowserNotification] Error showing notification:", error);
        return null;
    }
}

/**
 * Show admin notification in browser
 */
export function showAdminBrowserNotification(
    type: "vehicle" | "document" | "cancellation" | "user",
    message: string,
    actionLink?: string
): Notification | null {
    const titles: Record<typeof type, string> = {
        vehicle: "🚗 Nouveau véhicule à valider",
        document: "📄 Document en attente",
        cancellation: "❌ Demande d'annulation",
        user: "👤 Nouvelle inscription",
    };

    const notification = showBrowserNotification(titles[type], {
        body: message,
        tag: `admin-${type}`,
        requireInteraction: true,
        data: { actionLink },
    });

    // Handle click on notification
    if (notification && actionLink) {
        notification.onclick = () => {
            window.focus();
            window.location.href = actionLink;
            notification.close();
        };
    }

    return notification;
}
